import { Breadcrumbs, CreateButton, DynamicEntityIndex, FlashMessage, GlobalLoader, InputField, Modal, ModalAlert, SelectInput } from '@reactiveonline/reactive_admin_shared-packages'
import { emailValidation } from '@reactiveonline/reactive_admin_shared-packages/utils'
import GlobalImports from '../shared/GlobalImports'
import React, { useEffect, useRef, useState } from 'react'
import Toggle from "react-toggle"

export default function Index({
  appProps, administrators, adminRole, breadcrumbs, deleteAdministratorPath, entityPath, icon, initialCount, initialTotalPages, inviteAdministratorPath, listColumns, reinvitePath, roles, selectedColumns, updateAdministratorPath
}) {
  const [totalCount] = useState(initialCount)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [adminToBeDeleted, setAdminToBeDeleted] = useState(null)
  const [adminToBeEdited, setAdminToBeEdited] = useState(null)
  const [newAdministratorEmail, setNewAdministratorEmail] = useState('')
  const [newAdministratorName, setNewAdministratorName] = useState('')
  const [newAdministratorRole, setNewAdministratorRole] = useState(roles[0])
  const [newAdministratorDailyDigest, setNewAdministratorDailyDigest] = useState(false)
  const [newAdministratorWeeklyDigest, setNewAdministratorWeeklyDigest] = useState(false)
  const [whitelistedIps, setWhitelistedIps] = useState([])
  const [loading, setLoading] = useState(false)

  const flashMessageRef = useRef(null)

  useEffect(() => {
    if (adminToBeEdited) {
      setNewAdministratorName(adminToBeEdited.name ?? '')
      setNewAdministratorRole(roles.find(el => el.title === adminToBeEdited.role))
      setNewAdministratorDailyDigest(adminToBeEdited.daily_digest ?? false)
      setNewAdministratorWeeklyDigest(adminToBeEdited.weekly_digest ?? false)
      setWhitelistedIps(adminToBeEdited.whitelistedIps)
    }
  }, [adminToBeEdited])

  const closeModal = () => {
    setNewAdministratorEmail('')
    setNewAdministratorName('')
    setNewAdministratorRole(roles[0])
    setNewAdministratorDailyDigest(false)
    setNewAdministratorWeeklyDigest(false)
    setShowCreateModal(false)
  }

  const closeEditModal = () => {
    setNewAdministratorName('')
    setNewAdministratorRole(roles[0])
    setWhitelistedIps([])
    setNewAdministratorDailyDigest(false)
    setNewAdministratorWeeklyDigest(false)
    setAdminToBeEdited(null)
  }

  const handleDeleteAdministrator = () => {
    const fd = new FormData()

    fd.append('administrator_api_uid', adminToBeDeleted.apiUid)

    setAdminToBeDeleted(null)
    setLoading(true)

    Rails.ajax({
      type: 'DELETE',
      url: deleteAdministratorPath,
      dataType: 'json',
      data: fd,
      success: res => {
        flashMessageRef.current.show(res.message)
        window.location.reload()
      }
    })
  }

  const handleReinvite = administrator => {
    const fd = new FormData()

    fd.append('administrator_api_uid', administrator.apiUid)

    setLoading(true)

    Rails.ajax({
      type: 'POST',
      url: reinvitePath,
      dataType: 'json',
      data: fd,
      success: res => {
        setLoading(false)
        flashMessageRef.current.show(res.message)
      },
      error: res => {
        setLoading(false)
        flashMessageRef.current.show(res.error, 'error')
      }
    })
  }

  const inviteAdministrator = () => {
    const fd = new FormData()
    fd.append('admin_email', newAdministratorEmail)
    fd.append('admin_name', newAdministratorName)
    if (adminRole === 'superadmin') {
      fd.append('role_id', newAdministratorRole.value)
    } else {
      fd.append('role_id', roles.find(el => el.title === 'support_agent').value)
    }

    closeModal()
    setLoading(true)

    Rails.ajax({
      type: 'POST',
      url: inviteAdministratorPath,
      dataType: 'json',
      data: fd,
      success: res => {
        flashMessageRef.current.show(res.message)
        window.location.reload()
      },
      error: res => {
        flashMessageRef.current.show(res.message, 'error')
        setLoading(false)
      }
    })
  }

  const updateAdministrator = () => {
    const fd = new FormData()
    fd.append('administrator_api_uid', adminToBeEdited.apiUid)
    fd.append('administrator[name]', newAdministratorName)
    fd.append('administrator[role_id]', newAdministratorRole.value)
    fd.append('administrator[daily_digest]', newAdministratorDailyDigest)
    fd.append('administrator[weekly_digest]', newAdministratorWeeklyDigest)
    whitelistedIps.forEach((whitelistedIp, idx) => {
      const { id, ip, _destroy } = whitelistedIp

      if (id > 0) {
        fd.append(`administrator[whitelisted_ips_attributes][${ idx }][id]`, id)
      }

      if (_destroy || (id > 0 && !ip)) {
        fd.append(`administrator[whitelisted_ips_attributes][${ idx }][_destroy]`, true)
      } else {
        if (ip) {
          fd.append(`administrator[whitelisted_ips_attributes][${ idx }][ip]`, ip)
        }
      }
    })

    setLoading(true)

    Rails.ajax({
      type: 'PATCH',
      url: updateAdministratorPath,
      dataType: 'json',
      data: fd,
      success: res => {
        flashMessageRef.current.show(res.message)
        window.location.reload()
      },
      error: res => {
        flashMessageRef.current.show(res.error, 'error')
        setLoading(false)
      }
    })
  }

  const handleUpdateWhitelistedIp = (whitelistedIpId, value) => {
    const relevantIpIndex = whitelistedIps.findIndex(el => el.id === whitelistedIpId)

    setWhitelistedIps(prevState => [
      ...prevState.slice(0, relevantIpIndex),
      {
        ...prevState[relevantIpIndex],
        ip: value
      },
      ...prevState.slice(relevantIpIndex + 1)
    ])
  }

  const handleAddWhitelistedIp = () => {
    setWhitelistedIps(prevState => [
      ...prevState,
      {
        id: Math.min(...whitelistedIps.map(el => el.id), 0) - 1,
        ip: '',
      }
    ])
  }

  const handleDeleteWhitelistedIp = whitelistedIpId => {
    const relevantIpIndex = whitelistedIps.findIndex(el => el.id === whitelistedIpId)

    if (whitelistedIpId < 0) {
      setWhitelistedIps(prevState => [
        ...prevState.slice(0, relevantIpIndex),
        ...prevState.slice(relevantIpIndex + 1)
      ])
    } else {
      setWhitelistedIps(prevState => [
        ...prevState.slice(0, relevantIpIndex),
        {
          ...prevState[relevantIpIndex],
          _destroy: true
        },
        ...prevState.slice(relevantIpIndex + 1)
      ])
    }
  }

  const itemRenderer = (item, columns) => {
    return (
      <>
        { columns.map((column, index) => {
          if (column.label === 'email') {
            return (
              <div key={ column.label } className='item flex-box items-center flex-1'>
                <a href='#'>
                  { item[column.label] }
                </a>
                { item.canReinvite &&
                  <div className='tooltip-button'>
                    <i className='fa-light fa-circle-exclamation' style={{ color: '#f36944' }} />
                    <div className='tooltip'>
                      { Support.translations.administrator.invite_not_accepted }
                    </div>
                  </div>
                }
              </div>
            )
          } else {
            return (
              <div key={ column.label } className='item flex-1 flex-box items-center content-space-between'>
                { item[column.label] || '-' }

                { index === columns.length - 1 &&
                  <div className='flex-box items-center content-space-between' style={{ gap: '20px' }}>
                    <i
                      className='fa-light fa-pencil'
                      style={{ cursor: 'pointer' }}
                      onClick={ _e => setAdminToBeEdited(item) }
                    />
                    { item.canReinvite &&
                      <i
                        className='fa-light fa-envelope'
                        style={{ cursor: 'pointer' }}
                        onClick={ _e => handleReinvite(item) }
                      />
                    }
                    { item.canDelete &&
                      <i
                        className='fa-light fa-trash'
                        style={{ cursor: 'pointer' }}
                        onClick={ _e => setAdminToBeDeleted(item) }
                      />
                    }
                  </div>
                }
              </div>
            )
          }
        })}
      </>
    )
  }

  return (
    <>
      <div className='page-wrapper-header' style={{ gap: 10 }}>
        <div>
          <div className='page-header'>
            <i className={ `fa-light ${ icon }` }/>
            <h1 className='page-header-title'>
              { Support.translations.administrator.plural }
              <span className='m-l-10'>({ totalCount })</span>
            </h1>
          </div>

          <Breadcrumbs
            breadcrumbs={ breadcrumbs }
            wrapperHeader={ false }
          />
        </div>

        <CreateButton
          onClickCreate={ () => setShowCreateModal(true) }
          createTitle={ Support.translations.invite_administrator }
        />
      </div>

      <DynamicEntityIndex
        initialItems={ administrators }
        initialTotalPages={ initialTotalPages }
        initialTotalCount={ totalCount }
        fetchOnStart
        disableDelete={ true }
        disableCustomization={ true }
        entity={ 'administrators' }
        tableHeadings={ selectedColumns }
        itemRenderer={ itemRenderer }
        itemsPath={ entityPath }
        icon={ icon }
        noResourcesTitle={ Support.translations.no_administrators }
        listColumns={ listColumns }
        columnTranslations={ Support.translations.columns }
        headline={ Support.translations.administrator.plural }
      />

      { showCreateModal &&
        <Modal
          visible
          mode='large'
          closeModal={ closeModal }
          action={ inviteAdministrator }
          actionText={ ReactiveCore.translations.confirm }
          disableAction={ !emailValidation(newAdministratorEmail) }
        >
          <div className='flex-box flex-column flex-1'>
            <div className='bold' style={{ fontSize: 25 }}>
              { Support.translations.invite_administrator }
            </div>

            <div>
              <InputField
                className='col s12'
                label={ Support.translations.administrator.attributes.name }
              >
                <input
                  type='text'
                  value={ newAdministratorName }
                  onChange={ e => setNewAdministratorName(e.target.value) }
                />
              </InputField>
            </div>

            <div>
              <InputField
                className='col s12'
                label={ Support.translations.administrator.attributes.email }
              >
                <input
                  type='text'
                  value={ newAdministratorEmail }
                  onChange={ e => setNewAdministratorEmail(e.target.value) }
                />
              </InputField>
            </div>

            { adminRole === 'superadmin' &&
              <div>
                <InputField
                  className='col s12'
                  label={ Support.translations.role.model_name }
                >
                  <SelectInput
                    options={ roles }
                    currentOption={ newAdministratorRole.value }
                    setCurrentOption={ option => setNewAdministratorRole(option) }
                  />
                </InputField>
              </div>
            }

          </div>
        </Modal>
      }

      { !!adminToBeDeleted &&
        <ModalAlert
          alert={ Support.translations.destroy_administrator_alert }
          onSelect={ _e => handleDeleteAdministrator() }
          onClose={ () => setAdminToBeDeleted(null) }
        />
      }

      { !!adminToBeEdited &&
        <Modal
          visible
          mode='large'
          closeModal={ closeEditModal }
          action={ updateAdministrator }
          actionText={ ReactiveCore.translations.confirm }
        >
          <div className='flex-box flex-column flex-1'>
            <div className='bold' style={{ fontSize: 25 }}>
              { adminToBeEdited.email }
            </div>

            <div>
              <InputField
                className='col s12'
                label={ Support.translations.administrator.attributes.name }
              >
                <input
                  type='text'
                  value={ newAdministratorName }
                  onChange={ e => setNewAdministratorName(e.target.value) }
                />
              </InputField>
            </div>

            { adminRole === 'superadmin' &&
              <div>
                <InputField
                  className='col s12'
                  label={ Support.translations.role.model_name }
                >
                  <SelectInput
                    options={ roles }
                    currentOption={ newAdministratorRole.value }
                    setCurrentOption={ option => setNewAdministratorRole(option) }
                  />
                </InputField>
              </div>
            }

            { adminToBeEdited.role == 'superadmin' &&
              <>
                <div className='m-b-20 flex-box'>
                  <Toggle
                    icons={{ unchecked: null }}
                    className='reactToggle-element small'
                    checked={ newAdministratorDailyDigest || false }
                    onChange={ e => setNewAdministratorDailyDigest(e.target.checked) }
                  />
                  <div className="m-l-10">
                    <label>{ Support.translations.administrator.daily_digest }</label>
                  </div>
                </div>

                <div className='flex-box'>
                  <Toggle
                    icons={{ unchecked: null }}
                    className='reactToggle-element small'
                    checked={ newAdministratorWeeklyDigest || false }
                    onChange={ e => setNewAdministratorWeeklyDigest(e.target.checked) }
                  />
                  <div className="m-l-10">
                    <label>{ Support.translations.administrator.weekly_digest }</label>
                  </div>
                </div>
              </>
            }

            <div className='m-t-20'>
              <div>{ Support.translations.whitelisted_ips.plural }</div>

              <div>
                { whitelistedIps.filter(el => !el._destroy).map((whitelistedIp, index) => (
                  <div
                    key={ index }
                    className='flex-box items-center content-space-between'
                  >
                    <div>
                      <InputField
                        label={ Support.translations.whitelisted_ips.ip }
                      >
                        <input
                          type='text'
                          value={ whitelistedIp.ip }
                          onChange={ e => handleUpdateWhitelistedIp(whitelistedIp.id, e.target.value) }
                        />
                      </InputField>
                    </div>

                    <div
                      className='flex-box items-center'
                      style={{ cursor: 'pointer' }}
                      onClick={ _e => handleDeleteWhitelistedIp(whitelistedIp.id) }
                    >
                      <i
                        className='fa-light fa-trash'
                        style={{ fontSize: '16px' }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div
                className='flex-box content-end m-t-50'
              >
                <div
                  className='btn'
                  onClick={ handleAddWhitelistedIp }
                >
                  { Support.translations.add_new }
                </div>
              </div>

            </div>
          </div>

          { loading && <GlobalLoader fullPage /> }
        </Modal>
      }

      { loading && <GlobalLoader fullPage /> }

      <FlashMessage ref={ flashMessageRef } />
      <GlobalImports appProps={ appProps } />
    </>
  )
}
